import { useEffect, useState } from 'react';
import FormField from '../../components/FormField';
import { useAlert } from 'react-alert';
import { useNavigate, Link } from 'react-router-dom';
import './styles/User.scss'
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import { loadFaculties, getCachedFaculties } from '../../facultiesCache';
import ProductCatalog, { Pricing, formatPrice } from '../../components/ProductCatalog';
import { productNames } from '../../products';
import OnboardingWizard from '../../components/OnboardingWizard';

export default function User() {
    const[showWizard, setShowWizard] = useState(false);
    const[name, setName] = useState("");
    const[surname, setSurname] = useState("");
    const[email, setEmail] = useState("");
    const[password, setPassword] = useState("");
    const[passwordNew, setPasswordNew] = useState("");
    const[passwordRep, setPasswordRep] = useState("");
    const[fetched, setFetched] = useState(false);
    const[faculties, setFaculties] = useState<{id: string, name: string, examDate: string | null}[]>(getCachedFaculties() ?? []);
    const[facultyId, setFacultyId] = useState("");
    const[products, setProducts] = useState<{product: number, validTo: string | null}[]>([]);
    const[pricing, setPricing] = useState<Pricing[]>([]);
    const[upcomingPayments, setUpcomingPayments] = useState<{id: string, product: number, sequence: number, amount: number, dueDate: string}[]>([]);
    const[invoices, setInvoices] = useState<{id: string, number: string, amount: number, issuedAt: string}[]>([]);
    const alert = useAlert();
    const navigate = useNavigate();

    const api = new ApiHelper();

    async function fetch() {
        try {
            const res = await api.userApi.getDataList();
            const proc = await readJson(res);

            setName(proc.name);
            setSurname(proc.surname);
            setEmail(proc.email);
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function fetchGoal() {
        try {
            setFaculties(await loadFaculties(api));

            const goalRes = await api.userApi.getGoalList();
            const goal = await readJson(goalRes);
            if (goal) {
                setFacultyId(goal.id);
            }
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function fetchAccount() {
        try {
            const prodRes = await api.userApi.getProductsList();
            setProducts(await readJson(prodRes));

            await fetchUpcomingPayments();
            await fetchInvoices();
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function fetchInvoices() {
        try {
            const res = await api.documentsApi.invoicesList();
            setInvoices(await readJson(res));
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function downloadInvoice(inv : {id: string, number: string}) {
        try {
            await api.downloadInvoice(inv.id, inv.number + ".pdf");
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function fetchPricing() {
        try {
            const res = await api.pricingApi.getPricingList();
            setPricing(await readJson(res));
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    async function fetchUpcomingPayments() {
        try {
            const res = await api.ordersApi.getUpcomingPaymentsList();
            setUpcomingPayments(await readJson(res));
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    useEffect(() => {
        if (!fetched) {
            fetch();
            fetchGoal();
            fetchAccount();
            fetchPricing();
            setFetched(false);
        }
    }, []);

    async function processGoal() {
        if (!facultyId) {
            alert.error("Vyber fakultu");
            return;
        }

        try {
            await api.userApi.setGoalCreate({facultyId: facultyId});
            alert.success("Cíl byl úspěšně uložen");
        } catch (x : any) {
            api.processErrors(x);
        }
    }

    const formatDate = (d : string) => {
        const date = new Date(d);
        return date.getDate() + ". " + (date.getMonth() + 1) + ". " + date.getFullYear();
    }

    async function processData() {
        try {
            await api.userApi.changeDataCreate({name: name, surname: surname});
            alert.success("Data byla úspěšně změněna");
            fetch();
        } catch (x : any) {
            api.processErrors(x)
        }
    }

    async function processPassword() {
        if (passwordNew !== passwordRep) {
            alert.error("Hesla nejsou shodná");
            return;
        }

        try {
            await api.userApi.changePasswordCreate({password: password, newPassword: passwordNew});
            alert.success("Heslo bylo úspěšně změněno");
        } catch (x : any) {
            api.processErrors(x)
        }
    }

    async function processEmail() {
        try {
            await api.userApi.changeEmailCreate({email: email});
            alert.success("Na nový email byl odeslán ověřovací odkaz");
            alert.info("Po změně emailu je třeba ho znovu ověřit, než se budeš moci přihlásit.");
        } catch (x : any) {
            api.processErrors(x)
        }
    }

    function processLogOut() {
        localStorage.removeItem("token");
        navigate("/");
    }

    const selectedExamDate = faculties.find(f => f.id === facultyId)?.examDate ?? null;
    const examDays = selectedExamDate ? Math.ceil((new Date(selectedExamDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
    const displayName = (name || surname) ? (name + " " + surname).trim() : "Můj účet";

    return (
        <div className="account">
            <div className="card profile">
                <div className="pAvatar">
                    <img src="/images/pages/avatar.png" alt="" />
                </div>
                <div className="pInfo">
                    <h1>{displayName}</h1>
                    {email && <span>{email}</span>}
                </div>
                <div className="logout" onClick={() => processLogOut()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <path d="M16 17l5-5-5-5" /><path d="M21 12H9" />
                    </svg>
                    Odhlásit se
                </div>
            </div>

            <div className="grid">
                <section className="card">
                    <h2 className="cardTitle">Osobní údaje</h2>
                    <FormField label="jméno" value={name} handle={setName} />
                    <FormField label="příjmení" value={surname} handle={setSurname} />
                    <div className="button" onClick={() => processData()}>Uložit změny</div>
                </section>

                <section className="card">
                    <h2 className="cardTitle">Změna emailu</h2>
                    <FormField label="email" value={email} handle={setEmail} />
                    <p className="hint">Po změně emailu je nutné ho znovu ověřit.</p>
                    <div className="button" onClick={() => processEmail()}>Změnit email</div>
                </section>

                <section className="card">
                    <h2 className="cardTitle">Změna hesla</h2>
                    <FormField label="původní heslo" value={password} handle={setPassword} password={true} />
                    <FormField label="nové heslo" value={passwordNew} handle={setPasswordNew} password={true} />
                    <FormField label="heslo znovu" value={passwordRep} handle={setPasswordRep} password={true} />
                    <div className="button" onClick={() => processPassword()}>Změnit heslo</div>
                </section>

                <section className="card">
                    <h2 className="cardTitle">Můj cíl</h2>
                    <div className="goal">
                        <label>Cílová fakulta</label>
                        <select value={facultyId} onChange={(e) => setFacultyId(e.currentTarget.value)}>
                            <option value="">-- vyber fakultu --</option>
                            {faculties.map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </select>
                    </div>
                    {facultyId &&
                        <p className="examInfo">
                            {selectedExamDate
                                ? <>Termín přijímaček: <b>{formatDate(selectedExamDate)}</b>{examDays !== null && examDays >= 0 && <> — zbývá <b>{examDays} dní</b></>}</>
                                : <>Termín přijímaček zatím nebyl zveřejněn.</>
                            }
                        </p>
                    }
                    <div className="button" onClick={() => processGoal()}>Uložit cíl</div>
                </section>
            </div>

            <section className="card">
                <h2 className="cardTitle">Moje produkty</h2>
                <div className="products">
                    {products.length > 0 ?
                        products.map((p, i) => (
                            <div key={i} className="prodItem">
                                <span className="pIcon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                </span>
                                <div className="pText">
                                    <h3>{productNames[p.product] ?? "Produkt"}</h3>
                                    {p.validTo && <span>platí do {formatDate(p.validTo)}</span>}
                                </div>
                            </div>
                        ))
                        :
                        <p className="empty">Zatím nemáš žádný produkt.</p>
                    }
                </div>
            </section>

            <section className="card">
                <h2 className="cardTitle">Platby</h2>
                <div className="payments">
                    {upcomingPayments.length > 0 ?
                        upcomingPayments.map((p) => (
                            <Link key={p.id} to={"/platba/" + p.id} className="payItem">
                                <div className="info">
                                    <h3>{productNames[p.product] ?? "Produkt"}</h3>
                                    <span>platba č. {p.sequence} — splatnost {formatDate(p.dueDate)}</span>
                                </div>
                                <div className="amount">{formatPrice(p.amount)}</div>
                            </Link>
                        ))
                        :
                        <p className="empty">Nemáš žádné nadcházející platby.</p>
                    }
                </div>
            </section>

            <section className="card">
                <h2 className="cardTitle">Faktury</h2>
                <div className="payments">
                    {invoices.length > 0 ?
                        invoices.map((inv) => (
                            <div key={inv.id} className="payItem clickable" onClick={() => downloadInvoice(inv)}>
                                <div className="info">
                                    <h3>Faktura {inv.number}</h3>
                                    <span>vystaveno {formatDate(inv.issuedAt)} — stáhnout PDF</span>
                                </div>
                                <div className="amount">{formatPrice(inv.amount)}</div>
                            </div>
                        ))
                        :
                        <p className="empty">Zatím nemáš žádné faktury.</p>
                    }
                </div>
            </section>

            <section className="card">
                <h2 className="cardTitle">Úvodní průvodce</h2>
                <p className="hint">Projdi si znovu, co máš v kurzu k dispozici a jak to funguje.</p>
                <div className="button" onClick={() => setShowWizard(true)}>Spustit průvodce</div>
            </section>

            <section className="card">
                <h2 className="cardTitle">Dokup / upgrade</h2>
                <ProductCatalog pricing={pricing} ownedProducts={products.map((p) => p.product)} buyLabel="Dokoupit" />
            </section>

            {showWizard && <OnboardingWizard onClose={() => setShowWizard(false)} />}
        </div>
    );
}
